import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, API_BASE_URL } from '../config/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { Wrench, ArrowLeft, Loader2, Check, HardHat, Upload, FileText, Image, X, Star } from 'lucide-react';
import { toast } from 'sonner';

const serviceOptions = [
  { id: 'decorating', name: 'Decorating' },
  { id: 'carpentry', name: 'Carpentry' },
  { id: 'outdoors', name: 'Outdoors & Garden' },
  { id: 'electrics', name: 'Electrics' },
  { id: 'plastering', name: 'Plastering & Tiling' },
  { id: 'building', name: 'Building & Maintenance' },
  { id: 'general', name: 'General Tasks' },
  { id: 'cleaning', name: 'Cleaning Services' },
  { id: 'landlord', name: 'Landlord Services' }
];

const skillsList = [
  { id: 'painting', name: 'Painting & Decorating' },
  { id: 'woodwork', name: 'Woodwork & Carpentry' },
  { id: 'plumbing', name: 'Basic Plumbing' },
  { id: 'electrical', name: 'Electrical Work' },
  { id: 'tiling', name: 'Tiling' },
  { id: 'plastering', name: 'Plastering' },
  { id: 'flooring', name: 'Flooring Installation' },
  { id: 'kitchens', name: 'Kitchen Fitting' },
  { id: 'bathrooms', name: 'Bathroom Fitting' },
  { id: 'gardening', name: 'Gardening & Landscaping' },
  { id: 'fencing', name: 'Fencing & Decking' },
  { id: 'roofing', name: 'Roofing Repairs' },
  { id: 'gutters', name: 'Guttering' },
  { id: 'furniture', name: 'Furniture Assembly' },
  { id: 'hanging', name: 'Hanging & Mounting' }
];

export const HandymanApply = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [uploadingCV, setUploadingCV] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    location: '',
    services: [],
    experience_years: '',
    certifications: '',
    insurance_details: '',
    photo_url: '',
    bio: '',
    coverage_areas: '',
    cv_url: '',
    work_photos: [],
    skill_ratings: skillsList.map(s => ({ skill: s.name, rating: 5 })),
    preferred_work: [],
    // Right to work
    right_to_work: false,
    // Availability fields
    hours_per_week: '',
    availability_days: [],
    goals: '',
    // Transport fields
    has_driving_licence: '',
    has_vehicle: '',
    can_carry_equipment: false,
    // Tools
    has_own_tools: ''
  });

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handlePreferredToggle = (skillName) => {
    setFormData(prev => ({
      ...prev,
      preferred_work: prev.preferred_work.includes(skillName)
        ? prev.preferred_work.filter(s => s !== skillName)
        : [...prev.preferred_work, skillName]
    }));
  };

  const handleAvailabilityToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      availability_days: prev.availability_days.includes(day)
        ? prev.availability_days.filter(d => d !== day)
        : [...prev.availability_days, day]
    }));
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSkillRatingChange = (skillName, rating) => {
    setFormData(prev => ({
      ...prev,
      skill_ratings: prev.skill_ratings.map(s => 
        s.skill === skillName ? { ...s, rating } : s
      )
    }));
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or Word document');
      return;
    }

    setUploadingCV(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-document`, formDataUpload);
      setFormData(prev => ({ ...prev, cv_url: response.data.url }));
      toast.success('CV uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload CV');
    } finally {
      setUploadingCV(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhotos(true);
    const uploadedUrls = [];

    for (const file of files.slice(0, 10)) {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      try {
        const response = await axios.post(`${API}/upload`, formDataUpload);
        uploadedUrls.push(response.data.url);
      } catch (error) {
        console.error('Failed to upload photo:', error);
      }
    }

    if (uploadedUrls.length > 0) {
      setFormData(prev => ({ 
        ...prev, 
        work_photos: [...prev.work_photos, ...uploadedUrls].slice(0, 10)
      }));
      toast.success(`${uploadedUrls.length} photo(s) uploaded!`);
    }
    setUploadingPhotos(false);
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      work_photos: prev.work_photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.services.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/handyman/apply`, {
        ...formData,
        experience_years: parseInt(formData.experience_years) || 0
      });
      setSuccess(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const getSkillRating = (skillName) => {
    const skill = formData.skill_ratings.find(s => s.skill === skillName);
    return skill ? skill.rating : 5;
  };

  if (success) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 bg-muted/50">
        <Card className="max-w-md w-full border-border shadow-xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2 font-['Outfit']">Application Submitted!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you for applying to join Expert-Handyman. We'll review your application and get back to you within 2-3 business days.
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => navigate('/')} className="rounded-full" data-testid="back-home-btn">
                Back to Home
              </Button>
              <Button variant="outline" onClick={() => navigate('/handyman/login')} className="rounded-full" data-testid="handyman-login-btn">
                Already approved? Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-muted/50 py-8 md:py-12">
      <div className="max-w-3xl mx-auto px-4">
        <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <Card className="border-border shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
              <HardHat className="w-7 h-7 text-primary-foreground" />
            </div>
            <CardTitle className="text-2xl font-['Outfit']">Join Our Team</CardTitle>
            <CardDescription>We're looking for reliable, friendly people — experience is a bonus, not a requirement!</CardDescription>
          </CardHeader>

          {/* Progress Steps */}
          <div className="px-6 pb-4">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    {step}
                  </div>
                  {step < 3 && (
                    <div className={`w-20 sm:w-32 h-1 mx-2 ${
                      currentStep > step ? 'bg-primary' : 'bg-muted'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>Details</span>
              <span>Skills</span>
              <span>Portfolio</span>
            </div>
          </div>

          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Step 1: Personal Info & Services */}
              {currentStep === 1 && (
                <>
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg font-['Outfit']">Let's get to know you</h3>
                      <p className="text-sm text-muted-foreground">Just the basics to start — takes 2 minutes</p>
                      <p className="text-sm text-muted-foreground mt-3">
                        Do you feel you're particularly good at certain aspects of DIY or property work (for example hanging doors, decorating, repairs, etc.)? You don't need to do everything — if you can do a few things well and take pride in your work, we can help you build on that and develop your skills further.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          required
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                          required
                          className="h-11"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location">What area are you based in? (postcode or town) *</Label>
                      <Input
                        id="location"
                        placeholder="e.g., SK7 4SJ or Stockport"
                        value={formData.location}
                        onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                        required
                        className="h-11"
                        data-testid="location-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        required
                        className="h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Create Password *</Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        required
                        minLength={6}
                        className="h-11"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="experience">Years of Experience *</Label>
                        <Input
                          id="experience"
                          type="number"
                          min="0"
                          value={formData.experience_years}
                          onChange={(e) => setFormData(prev => ({ ...prev, experience_years: e.target.value }))}
                          required
                          className="h-11"
                        />
                        <p className="text-xs text-muted-foreground">New to the trade? No problem — put 0!</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="coverage">Coverage Areas</Label>
                        <Input
                          id="coverage"
                          placeholder="e.g., Manchester, Stockport"
                          value={formData.coverage_areas}
                          onChange={(e) => setFormData(prev => ({ ...prev, coverage_areas: e.target.value }))}
                          className="h-11"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg font-['Outfit']">Services You Offer *</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {serviceOptions.map((service) => (
                        <label
                          key={service.id}
                          className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                            formData.services.includes(service.id)
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <Checkbox
                            checked={formData.services.includes(service.id)}
                            onCheckedChange={() => handleServiceToggle(service.id)}
                          />
                          <span className="text-sm">{service.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Right to Work */}
                  <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <h3 className="font-semibold text-lg font-['Outfit'] text-amber-900">Right to Work *</h3>
                    <p className="text-sm text-amber-800">
                      By law, we must verify that all contractors have the legal right to work in the UK before any engagement.
                    </p>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={formData.right_to_work}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, right_to_work: checked }))}
                        className="mt-0.5"
                        data-testid="right-to-work-checkbox"
                      />
                      <span className="text-sm text-amber-900">
                        I confirm that I have the legal right to work in the United Kingdom and can provide appropriate documentation (e.g., UK/Irish passport, settled/pre-settled status, valid visa with work permission) upon request.
                      </span>
                    </label>
                  </div>

                  <p className="text-xs text-muted-foreground text-center">
                    You don't need to do everything — being good at a few things and doing them properly is what matters.
                  </p>

                  <Button 
                    type="button" 
                    onClick={() => {
                      if (!formData.name || !formData.email || !formData.password || !formData.phone || !formData.location || formData.services.length === 0) {
                        toast.error('Please fill in all required fields');
                        return;
                      }
                      if (!formData.right_to_work) {
                        toast.error('You must confirm your right to work in the UK');
                        return;
                      }
                      goToStep(2);
                    }}
                    className="w-full h-12 rounded-xl btn-accent"
                  >
                    Continue to Skills
                  </Button>
                </>
              )}

              {/* Step 2: Skills Self-Assessment */}
              {currentStep === 2 && (
                <>
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg font-['Outfit']">Your Skills & Transport</h3>
                      <p className="text-sm text-muted-foreground">
                        Be honest — it's OK to rate yourself low on some things, nobody's an expert at everything!
                      </p>
                    </div>
                    
                    <div className="space-y-6">
                      {skillsList.map((skill) => (
                        <div key={skill.id} className="space-y-2">
                          <div className="flex justify-between items-center">
                            <Label className="text-sm font-medium">{skill.name}</Label>
                            <div className="flex items-center gap-2">
                              <Star className={`w-4 h-4 ${getSkillRating(skill.name) >= 7 ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`} />
                              <span className="text-sm font-bold w-6 text-center">{getSkillRating(skill.name)}</span>
                            </div>
                          </div>
                          <Slider
                            value={[getSkillRating(skill.name)]}
                            onValueChange={([value]) => handleSkillRatingChange(skill.name, value)}
                            min={1}
                            max={10}
                            step={1}
                            className="w-full"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Beginner</span>
                            <span>Expert</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg font-['Outfit']">What do you prefer to do?</h3>
                    <p className="text-sm text-muted-foreground">
                      Select the types of work you enjoy most
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {skillsList.map((skill) => (
                        <label
                          key={skill.id}
                          className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                            formData.preferred_work.includes(skill.name)
                              ? 'border-green-500 bg-green-50'
                              : 'border-border hover:border-green-300'
                          }`}
                        >
                          <Checkbox
                            checked={formData.preferred_work.includes(skill.name)}
                            onCheckedChange={() => handlePreferredToggle(skill.name)}
                          />
                          <span className="text-sm">{skill.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Transport Section */}
                  <div className="space-y-4 pt-4 border-t">
                    <div>
                      <h3 className="font-semibold text-lg font-['Outfit']">Getting to Jobs</h3>
                      <p className="text-sm text-muted-foreground">
                        This helps us match you with jobs you can easily reach
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Do you have a full UK driving licence?</Label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' },
                          { value: 'provisional', label: 'Provisional' }
                        ].map((option) => (
                          <label
                            key={option.value}
                            className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all text-center ${
                              formData.has_driving_licence === option.value
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="driving_licence"
                              value={option.value}
                              checked={formData.has_driving_licence === option.value}
                              onChange={(e) => setFormData(prev => ({ ...prev, has_driving_licence: e.target.value }))}
                              className="sr-only"
                            />
                            <span className="text-sm font-medium">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Do you have your own vehicle?</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {[
                          { value: 'van', label: '🚐 Van' },
                          { value: 'car', label: '🚗 Car' },
                          { value: 'motorbike', label: '🏍️ Motorbike' },
                          { value: 'public_transport', label: '🚌 Public Transport' },
                          { value: 'lifts', label: '🤝 Can Get Lifts' }
                        ].map((option) => (
                          <label
                            key={option.value}
                            className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all text-center ${
                              formData.has_vehicle === option.value
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="vehicle"
                              value={option.value}
                              checked={formData.has_vehicle === option.value}
                              onChange={(e) => setFormData(prev => ({ ...prev, has_vehicle: e.target.value }))}
                              className="sr-only"
                            />
                            <span className="text-sm">{option.label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Don't have a vehicle? No problem — we have jobs accessible by public transport and can arrange lifts to jobs when needed.
                      </p>
                    </div>

                    {(formData.has_vehicle === 'van' || formData.has_vehicle === 'car') && (
                      <label className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:border-primary/50">
                        <Checkbox
                          checked={formData.can_carry_equipment}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, can_carry_equipment: checked }))}
                        />
                        <span className="text-sm">I can carry ladders, paint, and equipment</span>
                      </label>
                    )}

                    {/* Tools Section */}
                    <div className="space-y-2 pt-4 border-t">
                      <Label>Do you have your own tools?</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                          { value: 'full_kit', label: '🧰 Yes - Full Tool Kit' },
                          { value: 'basic', label: '🔧 Some Basic Tools' },
                          { value: 'none', label: '📦 Need Tools Supplied' }
                        ].map((option) => (
                          <label
                            key={option.value}
                            className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all text-center ${
                              formData.has_own_tools === option.value
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="tools"
                              value={option.value}
                              checked={formData.has_own_tools === option.value}
                              onChange={(e) => setFormData(prev => ({ ...prev, has_own_tools: e.target.value }))}
                              className="sr-only"
                            />
                            <span className="text-sm">{option.label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Don't have tools? No problem — we can supply what you need for jobs.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => goToStep(1)}
                      className="flex-1 h-12 rounded-xl"
                    >
                      Back
                    </Button>
                    <Button 
                      type="button" 
                      onClick={() => goToStep(3)}
                      className="flex-1 h-12 rounded-xl btn-accent"
                    >
                      Continue to Portfolio
                    </Button>
                  </div>
                </>
              )}

              {/* Step 3: CV, Photos & Additional Info */}
              {currentStep === 3 && (
                <>
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg font-['Outfit']">Show us what you've got</h3>
                      <p className="text-sm text-muted-foreground">Everything here is optional — add what you can, skip what you can't</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-lg font-['Outfit']">Upload Your CV</h3>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">Optional</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Don't have a CV? That's completely fine — many great tradespeople don't. Just tell us about yourself in the bio section below instead.
                    </p>
                    <div className="border-2 border-dashed border-border rounded-xl p-6 text-center">
                      {formData.cv_url ? (
                        <div className="flex items-center justify-center gap-3">
                          <FileText className="w-8 h-8 text-green-600" />
                          <div>
                            <p className="font-medium text-green-600">CV Uploaded</p>
                            <Button 
                              type="button" 
                              variant="link" 
                              size="sm"
                              onClick={() => setFormData(prev => ({ ...prev, cv_url: '' }))}
                              className="text-red-500 p-0 h-auto"
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            id="cv-upload"
                            accept=".pdf,.doc,.docx"
                            onChange={handleCVUpload}
                            className="hidden"
                          />
                          <label htmlFor="cv-upload" className="cursor-pointer">
                            <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                            <p className="font-medium">Upload CV</p>
                            <p className="text-sm text-muted-foreground">PDF or Word document</p>
                            {uploadingCV && <Loader2 className="w-5 h-5 animate-spin mx-auto mt-2" />}
                          </label>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-lg font-['Outfit']">Photos of Your Work</h3>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">Optional</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Got photos of jobs you've done? Great! If not, don't worry — you can always add these later once you're approved.
                    </p>
                    
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                      {formData.work_photos.map((url, index) => (
                        <div key={index} className="relative aspect-square rounded-lg overflow-hidden group">
                          <img 
                            src={`${API_BASE_URL}${url}`} 
                            alt={`Work ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      ))}
                      
                      {formData.work_photos.length < 10 && (
                        <label className="aspect-square rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handlePhotoUpload}
                            className="hidden"
                          />
                          {uploadingPhotos ? (
                            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                          ) : (
                            <>
                              <Image className="w-6 h-6 text-muted-foreground mb-1" />
                              <span className="text-xs text-muted-foreground">Add Photos</span>
                            </>
                          )}
                        </label>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg font-['Outfit']">Additional Information</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="certifications">Certifications & Qualifications</Label>
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">Optional</span>
                      </div>
                      <Textarea
                        id="certifications"
                        placeholder="List any relevant certifications, NVQs, or qualifications..."
                        value={formData.certifications}
                        onChange={(e) => setFormData(prev => ({ ...prev, certifications: e.target.value }))}
                        className="min-h-20"
                      />
                      <p className="text-xs text-muted-foreground">No formal qualifications? That's OK — experience and attitude matter more to us.</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="insurance">Insurance Details</Label>
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">Optional</span>
                      </div>
                      <Textarea
                        id="insurance"
                        placeholder="Public liability insurance, employer's liability, etc..."
                        value={formData.insurance_details}
                        onChange={(e) => setFormData(prev => ({ ...prev, insurance_details: e.target.value }))}
                        className="min-h-20"
                      />
                      <p className="text-xs text-muted-foreground">Don't have insurance yet? We can discuss options if you're approved.</p>
                    </div>
                  </div>

                  {/* Availability Section */}
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-semibold text-lg font-['Outfit']">Availability & Goals</h3>
                    
                    <div className="space-y-2">
                      <Label htmlFor="hours_per_week">How many hours per week can you work?</Label>
                      <Input
                        id="hours_per_week"
                        placeholder="e.g., 20-30 hours, flexible, weekends only"
                        value={formData.hours_per_week}
                        onChange={(e) => setFormData(prev => ({ ...prev, hours_per_week: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground">Looking for full-time, part-time, or just occasional work? All good with us.</p>
                    </div>

                    <div className="space-y-2">
                      <Label>When are you available to work?</Label>
                      <div className="flex flex-wrap gap-2">
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                          <label 
                            key={day}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                              formData.availability_days.includes(day)
                                ? 'border-primary bg-primary/10'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            <Checkbox
                              checked={formData.availability_days.includes(day)}
                              onCheckedChange={() => handleAvailabilityToggle(day)}
                            />
                            <span className="text-sm">{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="goals">What do you want from working with Expert-Handyman?</Label>
                      <Textarea
                        id="goals"
                        placeholder="Tell us your goals - steady income, flexible work, building experience, extra cash on weekends..."
                        value={formData.goals}
                        onChange={(e) => setFormData(prev => ({ ...prev, goals: e.target.value }))}
                        className="min-h-24"
                      />
                      <p className="text-xs text-muted-foreground">Be honest — it helps us find the right fit for you.</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bio">About You</Label>
                      <Textarea
                        id="bio"
                        placeholder="Tell us about yourself — your background, what you enjoy about the trade, why you'd be great to work with..."
                        value={formData.bio}
                        onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                        className="min-h-24"
                      />
                      <p className="text-xs text-muted-foreground">This is your chance to stand out, especially if you don't have a CV or formal qualifications.</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => goToStep(2)}
                      className="flex-1 h-12 rounded-xl"
                    >
                      Back
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 h-12 rounded-xl btn-accent" 
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Application'}
                    </Button>
                  </div>
                </>
              )}
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              Already registered?{' '}
              <Link to="/handyman/login" className="text-primary font-medium hover:underline">
                Login here
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
